import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { authApi } from '../../auth/api';
import { settingsApi, SettingUpdate } from '../../auth/settings.api';
import { companiesApi } from '../../companies/api';
import { notify } from '../../../core/notifications/notify';
import { logClientError } from '../../../utils/globalError';
import ChangeNotificationPreferences from '../../../components/settings/ChangeNotificationPreferences';
import { useModal, ConfirmModal } from '../../../components/modal';

import {
  User,
  Settings as SettingsIcon,
  Bell,
  Shield,
  Building2
} from 'lucide-react';
import PageInfoButton from '@/components/PageInfoButton';

// Type definitions for comprehensive settings management
interface SystemSettings {
  siteName: string;
  siteUrl: string;
  supportEmail: string;
  timezone: string;
  dateFormat: string;
  language: string;
}

interface NotificationSettings {
  emailNotifications: boolean;
  pushNotifications: boolean;
  weeklyDigest: boolean;
  securityAlerts: boolean;
  darkMode: boolean;
}

interface SecuritySettings {
  twoFactorEnabled: boolean;
  twoFactorMethod: 'totp' | 'email' | 'sms';
  sessionTimeout: number;
  passwordMinLength: number;
  requireStrongPassword: boolean;
  lockoutMaxAttempts: number;
  lockoutDurationMinutes: number;
  trustedDeviceDays: number;
  requirePasswordRotationDays: number;
}

interface ProfileSettings {
  first_name: string;
  last_name: string;
  email: string;
  profilePictureUrl?: string;
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
  bio?: string;
}

interface CompanySettings {
  companyName: string;
  companyEmail: string;
  industry: string;
  complianceDeadline: string;
  autoAssignFrameworks: boolean;
}

interface SettingsState {
  profile: ProfileSettings;
  system: SystemSettings;
  notifications: NotificationSettings;
  security: SecuritySettings;
  company: CompanySettings;
}

const AccountSettings: React.FC = () => {
  const { user, hasPermission } = useAuth();
  const { openModal } = useModal();
  const [searchParams, setSearchParams] = useSearchParams();

  const isSystemAdmin = hasPermission('system.config');
  const isCompanyUser = !!user?.company_id;

  const allowedTabs = ['profile', 'security', 'notifications',
    ...(isSystemAdmin ? ['system'] : []),
    ...(isCompanyUser ? ['company'] : [])
  ];

  const [activeTab, setActiveTab] = useState<string>(() => {
    const tab = searchParams.get('tab');
    return tab && allowedTabs.includes(tab) ? tab : 'profile';
  });

  const [saving, setSaving] = useState(false);
  const [qrCodeData, setQrCodeData] = useState<string | null>(null); // For MFA setup
  const [twoFactorSecret, setTwoFactorSecret] = useState<string | null>(null);
  const [twoFactorToken, setTwoFactorToken] = useState<string>('');
  const [is2FASectionExpanded, setIs2FASectionExpanded] = useState<boolean>(false);
  const [userSessions, setUserSessions] = useState<any[]>([]);

  // Initialize settings state (matching your provided structure)
  const [settings, setSettings] = useState<SettingsState>({
    profile: {
      first_name: '',
      last_name: '',
      email: '',
      profilePictureUrl: '',
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      bio: ''
    },
    system: {
      siteName: '',
      siteUrl: '',
      supportEmail: '',
      timezone: '',
      dateFormat: '',
      language: ''
    },
    notifications: {
      emailNotifications: false,
      pushNotifications: false,
      weeklyDigest: false,
      securityAlerts: false,
      darkMode: false
    },
    security: {
      twoFactorEnabled: false,
      twoFactorMethod: 'totp',
      sessionTimeout: 30,
      passwordMinLength: 8,
      requireStrongPassword: false,
      lockoutMaxAttempts: 5,
      lockoutDurationMinutes: 15,
      trustedDeviceDays: 30,
      requirePasswordRotationDays: 0
    },
    company: {
      companyName: '',
      companyEmail: '',
      industry: '',
      complianceDeadline: '',
      autoAssignFrameworks: false
    }
  });

  const loadSettings = useCallback(async () => {
    if (!user) return;
    try {
      const [userSettings, systemConfigs, twoFactorStatus, twoFactorSettings, currentUser, company, sessions] = await Promise.all([
        settingsApi.getUserSettings(),
        settingsApi.getSystemConfigs(),
        authApi.getTwoFactorStatus(),
        authApi.getTwoFactorSettings(),
        authApi.getMe(),
        user.company_id ? companiesApi.getCompany(user.company_id) : null,
        authApi.getUserSessions()
      ]);

      // Create a map of settings by key for easy lookup
      const settingsMap = userSettings.reduce((acc, setting) => {
        acc[setting.setting_key] = setting.setting_value;
        return acc;
      }, {} as Record<string, any>);

      // Create a map of system configs by key
      const systemConfigMap = systemConfigs.reduce((acc, config) => {
        acc[config.config_key] = config.config_value;
        return acc;
      }, {} as Record<string, any>);

      setSettings({
        profile: {
          first_name: currentUser.first_name || '',
          last_name: currentUser.last_name || '',
          email: currentUser.email || '',
          profilePictureUrl: currentUser.profile_picture_url || currentUser.profile_image || '',
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
          bio: settingsMap.bio || ''
        },
        system: {
          siteName: systemConfigMap.site_title || 'EIS Tech',
          siteUrl: systemConfigMap.site_url || 'https://eis-tech.co.uk',
          supportEmail: systemConfigMap.support_email || 'support@eis-tech.co.uk',
          timezone: systemConfigMap.timezone || 'UTC',
          dateFormat: systemConfigMap.date_format || 'YYYY-MM-DD',
          language: systemConfigMap.language || 'en'
        },
        notifications: {
          emailNotifications: settingsMap.email_notifications !== undefined ? settingsMap.email_notifications : true,
          pushNotifications: settingsMap.push_notifications !== undefined ? settingsMap.push_notifications : false,
          weeklyDigest: settingsMap.weekly_digest !== undefined ? settingsMap.weekly_digest : true,
          securityAlerts: settingsMap.security_alerts !== undefined ? settingsMap.security_alerts : true,
          darkMode: settingsMap.dark_mode !== undefined ? settingsMap.dark_mode : false
        },
        security: {
          twoFactorEnabled: twoFactorStatus.enabled,
          twoFactorMethod: twoFactorSettings?.method || 'totp',
          sessionTimeout: parseInt(settingsMap.session_timeout) || 30,
          passwordMinLength: parseInt(settingsMap.password_min_length) || 8,
          requireStrongPassword: settingsMap.require_strong_password !== undefined ? settingsMap.require_strong_password : true,
          lockoutMaxAttempts: parseInt(settingsMap.lockout_max_attempts) || 5,
          lockoutDurationMinutes: parseInt(settingsMap.lockout_duration_minutes) || 15,
          trustedDeviceDays: parseInt(settingsMap.trusted_device_days) || 30,
          requirePasswordRotationDays: parseInt(settingsMap.require_password_rotation_days) || 0
        },
        company: {
          companyName: company?.name || 'EIS Tech Ltd',
          companyEmail: company?.email || 'info@eis-tech.co.uk',
          industry: company?.industry || 'Technology',
          complianceDeadline: settingsMap.compliance_deadline || '2024-12-31',
          autoAssignFrameworks: settingsMap.auto_assign_frameworks !== undefined ? settingsMap.auto_assign_frameworks : true
        }
      });

      // Set user sessions
      setUserSessions(sessions);

      // Expand 2FA section if already enabled
      if (twoFactorStatus.enabled) {
        setIs2FASectionExpanded(true);
      }

      // Expand 2FA section if already enabled
      if (twoFactorStatus.enabled) {
        setIs2FASectionExpanded(true);
      }
    } catch (error) {
      logClientError(error as Error, { context: 'loadSettings', userId: user?.id });
      notify.error('Failed to load settings');
    }
  }, [user]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && ['profile', 'security', 'system', 'notifications', 'company'].includes(tab)) {
      setActiveTab(tab);
    }
  }, [searchParams]);

  const handleEnableMethod = async (method: 'totp' | 'email') => {
    if (method === 'email') {
      // Email: enable immediately
      setSettings(prev => ({ ...prev, security: { ...prev.security, twoFactorEnabled: true, twoFactorMethod: 'email' } }));
      try {
        await authApi.updateTwoFactorSettings({ method: 'email' });
        await authApi.setupTwoFactor();
        await authApi.enableTwoFactor('');
        notify.success('Email 2FA enabled');
      } catch (error) {
        console.error('Failed to enable email 2FA:', error);
        notify.error('Failed to enable 2FA');
        logClientError(error as Error, { context: 'handleEnableMethod_email', userId: user?.id });
        setSettings(prev => ({ ...prev, security: { ...prev.security, twoFactorEnabled: false } }));
      }
    } else if (method === 'totp') {
      // TOTP: show QR code setup, user must confirm with a code
      setSettings(prev => ({ ...prev, security: { ...prev.security, twoFactorMethod: 'totp' } }));
      setIs2FASectionExpanded(true);
      try {
        await authApi.updateTwoFactorSettings({ method: 'totp' });
        const setupData = await authApi.setupTwoFactor();
        setQrCodeData(setupData.qrCode);
        setTwoFactorSecret(setupData.secret);
      } catch (error) {
        console.error('Failed to setup TOTP:', error);
        notify.error('Failed to setup authenticator app');
        logClientError(error as Error, { context: 'handleEnableMethod_totp', userId: user?.id });
        setIs2FASectionExpanded(false);
      }
    }
  };

  const handleDisable2FA = async () => {
    setSettings(prev => ({ ...prev, security: { ...prev.security, twoFactorEnabled: false } }));
    setIs2FASectionExpanded(false);
    setQrCodeData(null);
    setTwoFactorSecret(null);
    setTwoFactorToken('');
    try {
      await authApi.disableTwoFactor();
      notify.success('Two-factor authentication disabled');
    } catch (error) {
      console.error('Failed to disable 2FA:', error);
      notify.error('Failed to disable 2FA');
      logClientError(error as Error, { context: 'handleDisable2FA', userId: user?.id });
      setSettings(prev => ({ ...prev, security: { ...prev.security, twoFactorEnabled: true } }));
    }
  };

  const handleConfirmTwoFactor = async () => {
    try {
      await authApi.enableTwoFactor(twoFactorToken);
      setSettings(prev => ({
        ...prev,
        security: { ...prev.security, twoFactorEnabled: true }
      }));
      setQrCodeData(null);
      setTwoFactorSecret(null);
      setTwoFactorToken('');
      setIs2FASectionExpanded(false); // Close the section after successful setup
      notify.success('Two-factor authentication enabled');
    } catch (error) {
      notify.error('Invalid token. Please try again.');
      logClientError(error as Error, { context: 'handleConfirmTwoFactor', userId: user?.id });
    }
  };

  const handleProfilePictureUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const result = await authApi.uploadProfileImage(file);
      const newUrl = result.profile_picture_url;
      setSettings(prev => ({
        ...prev,
        profile: { ...prev.profile, profilePictureUrl: newUrl }
      }));

      // Update localStorage user so AuthContext and all avatars refresh immediately
      try {
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const cachedUser = JSON.parse(userStr);
          cachedUser.profile_picture_url = newUrl;
          localStorage.setItem('user', JSON.stringify(cachedUser));
          // Force AuthContext to re-read (trigger re-render across system)
          window.dispatchEvent(new StorageEvent('storage', { key: 'user' }));
        }
      } catch {}

      notify.success('Profile picture updated successfully');
    } catch (error) {
      notify.error('Failed to upload profile picture');
      logClientError(error as Error, { context: 'handleProfilePictureUpload', userId: user?.id });
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    try {
      await authApi.revokeSession(sessionId);
      // Refresh sessions list
      const updatedSessions = await authApi.getUserSessions();
      setUserSessions(updatedSessions);
      notify.success('Session revoked successfully');
    } catch (error) {
      notify.error('Failed to revoke session');
      logClientError(error as Error, { context: 'handleRevokeSession', userId: user?.id });
    }
  };

  const handleSave = async (category: keyof SettingsState) => {
    if (!user) return;
    setSaving(true);
    try {
      // Mapping updates based on category
      if (category === 'profile') {
        // Update user profile via auth API
        await authApi.updateUser(user.id, {
          first_name: settings.profile.first_name,
          last_name: settings.profile.last_name,
          email: settings.profile.email
        });

        // Update bio via settings API
        if (settings.profile.bio !== undefined) {
          await settingsApi.updateUserSettings([{
            key: 'bio',
            value: settings.profile.bio,
            type: 'string'
          }]);
        }
      } else if (category === 'system') {
        const systemUpdates: SettingUpdate[] = [
          { key: 'site_title', value: settings.system.siteName, type: 'string' },
          { key: 'site_url', value: settings.system.siteUrl, type: 'string' },
          { key: 'support_email', value: settings.system.supportEmail, type: 'string' },
          { key: 'timezone', value: settings.system.timezone, type: 'string' },
          { key: 'date_format', value: settings.system.dateFormat, type: 'string' },
          { key: 'language', value: settings.system.language, type: 'string' }
        ];
        await settingsApi.updateSystemConfigs(systemUpdates);
      } else if (category === 'notifications') {
        const notificationUpdates: SettingUpdate[] = [
          { key: 'email_notifications', value: settings.notifications.emailNotifications, type: 'boolean' },
          { key: 'push_notifications', value: settings.notifications.pushNotifications, type: 'boolean' },
          { key: 'weekly_digest', value: settings.notifications.weeklyDigest, type: 'boolean' },
          { key: 'security_alerts', value: settings.notifications.securityAlerts, type: 'boolean' },
          { key: 'dark_mode', value: settings.notifications.darkMode, type: 'boolean' }
        ];
        await settingsApi.updateUserSettings(notificationUpdates);
      } else if (category === 'security') {
        const securityUpdates: SettingUpdate[] = [
          { key: 'session_timeout', value: settings.security.sessionTimeout, type: 'number' },
          { key: 'password_min_length', value: settings.security.passwordMinLength, type: 'number' },
          { key: 'require_strong_password', value: settings.security.requireStrongPassword, type: 'boolean' },
          { key: 'lockout_max_attempts', value: Math.min(10, Math.max(1, settings.security.lockoutMaxAttempts)), type: 'number' },
          { key: 'lockout_duration_minutes', value: Math.min(120, Math.max(1, settings.security.lockoutDurationMinutes)), type: 'number' },
          { key: 'trusted_device_days', value: Math.min(365, Math.max(0, settings.security.trustedDeviceDays)), type: 'number' },
          { key: 'require_password_rotation_days', value: Math.min(365, Math.max(0, settings.security.requirePasswordRotationDays)), type: 'number' }
        ];
        await settingsApi.updateUserSettings(securityUpdates);
      } else if (category === 'company') {
        // Update company data via companies API
        if (user.company_id) {
          await companiesApi.updateCompany(user.company_id, {
            name: settings.company.companyName,
            email: settings.company.companyEmail,
            industry: settings.company.industry
          });
        }

        // Update user-specific company settings
        const companyUpdates: SettingUpdate[] = [
          { key: 'compliance_deadline', value: settings.company.complianceDeadline, type: 'string' },
          { key: 'auto_assign_frameworks', value: settings.company.autoAssignFrameworks, type: 'boolean' }
        ];
        await settingsApi.updateUserSettings(companyUpdates);
      }

      notify.success(`${category.charAt(0).toUpperCase() + category.slice(1)} updated`);
    } catch (error) {
      notify.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  // --- RENDERING SECTIONS ---

  const renderProfileSection = () => (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
              <User className="text-white" size={16} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900 text-left">User Profile</h3>
              <p className="text-xs text-slate-500 text-left">Manage your personal information</p>
            </div>
          </div>
          <button
            onClick={() => handleSave('profile')}
            disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Update Profile
          </button>
        </div>

        <div className="p-6">
          <div className="flex items-start gap-6">
            <div className="relative group">
              <div className="h-24 w-24 rounded-xl bg-slate-100 border border-slate-200 overflow-hidden flex items-center justify-center text-3xl font-bold text-slate-300">
                {settings.profile.profilePictureUrl ? (
                  <img
                    src={settings.profile.profilePictureUrl}
                    alt="Profile"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
                  </svg>
                )}
              </div>
              <label className="absolute inset-0 flex items-center justify-center bg-black/40 text-white opacity-0 group-hover:opacity-100 rounded-xl cursor-pointer transition text-xs font-medium">
                Change
                <input type="file" className="hidden" accept="image/*" onChange={handleProfilePictureUpload} />
              </label>
            </div>
            <div className="flex-1 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name</label>
                  <input
                    type="text"
                    value={`${settings.profile.first_name} ${settings.profile.last_name}`}
                    onChange={(e) => {
                      const [first = '', ...rest] = e.target.value.split(' ');
                      setSettings(prev => ({
                        ...prev,
                        profile: { ...prev.profile, first_name: first, last_name: rest.join(' ') }
                      }));
                    }}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Preferred Alias</label>
                  <input
                    type="text"
                    value={settings.profile.email.split('@')[0]}
                    onChange={(e) =>
                      setSettings(prev => ({
                        ...prev,
                        profile: { ...prev.profile, email: `${e.target.value}@${settings.profile.email.split('@')[1]}` }
                      }))
                    }
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5">Bio</label>
                <textarea
                  value={settings.profile.bio || ''}
                  onChange={(e) =>
                    setSettings(prev => ({
                      ...prev,
                      profile: { ...prev.profile, bio: e.target.value }
                    }))
                  }
                  className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                  rows={3}
                  placeholder="Enter brief professional bio..."
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Organizational Placement */}
      <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
              <Building2 className="text-white" size={16} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900 text-left">Organizational Placement</h3>
              <p className="text-xs text-slate-500 text-left">Your current role and company information</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Company</p>
                <p className="text-base font-semibold text-slate-900">{user?.company?.name || 'No Company Assigned'}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">System Role</p>
                <p className="text-base font-semibold text-blue-600">{user?.roles?.join(', ') || 'User'}</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Account Status</p>
                <p className="text-base font-semibold text-slate-900">{user?.is_active ? 'Active' : 'Inactive'}</p>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-1">Verification Status</p>
                <span className={`inline-flex items-center px-3 py-1 rounded-lg text-xs font-semibold ${
                  user?.email_verified
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-amber-100 text-amber-700'
                }`}>
                  {user?.email_verified ? 'VERIFIED' : 'PENDING'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderSecuritySection = () => (
    <div className="space-y-6">
      {/* Security Header */}
      <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-emerald-600 rounded-lg flex items-center justify-center">
              <Shield className="text-white" size={16} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-900 text-left">Account Governance</h3>
              <p className="text-xs text-slate-500 text-left">
                ID: {user?.id} | Security: {settings.security.twoFactorEnabled ? 'HIGH' : 'MEDIUM'}
              </p>
            </div>
          </div>
          <button
            onClick={() => handleSave('security')}
            disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Save Security Settings
          </button>
        </div>
      </div>

      {/* Credential Rotation */}
      <div>
        <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-left">Credential Rotation</h2>
        <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <div className="grid grid-cols-3 gap-8">
            <div className="col-span-1">
              <label className="block text-sm font-semibold text-slate-700 mb-1 text-left">Change Password</label>
              <p className="text-xs text-slate-500 leading-relaxed text-left">Keep your account secure by updating your password regularly.</p>
            </div>
            <div className="col-span-2 space-y-3">
              <input
                type="password"
                placeholder="Current Password"
                value={settings.profile.currentPassword}
                onChange={(e) =>
                  setSettings(prev => ({
                    ...prev,
                    profile: { ...prev.profile, currentPassword: e.target.value }
                  }))
                }
                className="w-full border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="relative">
                <input
                  type="password"
                  placeholder="New Password"
                  value={settings.profile.newPassword}
                  onChange={(e) =>
                    setSettings(prev => ({
                      ...prev,
                      profile: { ...prev.profile, newPassword: e.target.value }
                    }))
                  }
                  className="w-full border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <div className="mt-2 h-1.5 w-full bg-slate-100 rounded-full overflow-hidden flex">
                  <div className={`h-full rounded-full transition-all ${
                    settings.profile.newPassword.length === 0 ? 'bg-slate-200 w-0' :
                    settings.profile.newPassword.length < 8 ? 'bg-red-500 w-1/4' :
                    settings.profile.newPassword.length < 12 ? 'bg-yellow-500 w-1/2' :
                    'bg-emerald-500 w-full'
                  }`}></div>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {settings.profile.newPassword.length === 0 ? 'Enter a new password' :
                   settings.profile.newPassword.length < 8 ? 'Password too weak (min 8 chars)' :
                   settings.profile.newPassword.length < 12 ? 'Password good' :
                   'Password strong'}
                </p>
              </div>
              <input
                type="password"
                placeholder="Confirm New Password"
                value={settings.profile.confirmPassword}
                onChange={(e) =>
                  setSettings(prev => ({
                    ...prev,
                    profile: { ...prev.profile, confirmPassword: e.target.value }
                  }))
                }
                className="w-full border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <div className="flex gap-3 items-center">
                <button
                  onClick={async () => {
                    if (!settings.profile.currentPassword || !settings.profile.newPassword) {
                      notify.error('Please fill in current and new password fields');
                      return;
                    }

                    if (settings.profile.newPassword !== settings.profile.confirmPassword) {
                      notify.error('New passwords do not match');
                      return;
                    }

                    if (settings.profile.newPassword.length < 8) {
                      notify.error('New password must be at least 8 characters long');
                      return;
                    }

                    try {
                      const response = await fetch('/api/auth/change-password', {
                        method: 'POST',
                        headers: {
                          'Content-Type': 'application/json',
                          'Authorization': `Bearer ${localStorage.getItem('token')}`
                        },
                        body: JSON.stringify({
                          currentPassword: settings.profile.currentPassword,
                          newPassword: settings.profile.newPassword
                        })
                      });

                      const result = await response.json();

                      if (result.success) {
                        notify.success('Password changed successfully!');
                        setSettings(prev => ({
                          ...prev,
                          profile: {
                            ...prev.profile,
                            currentPassword: '',
                            newPassword: '',
                            confirmPassword: ''
                          }
                        }));
                      } else {
                        notify.error(result.message || 'Failed to change password');
                      }
                    } catch (error) {
                      console.error('Error changing password:', error);
                      notify.error('Failed to change password');
                    }
                  }}
                  className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                >
                  Change Password
                </button>
                <button
                  onClick={() => {
                    openModal({
                      component: ConfirmModal,
                      props: {
                        title: 'Request Password Reset',
                        message: 'Are you sure you want to request a password reset? You will receive an email with a secure reset link.',
                        confirmText: 'Send Reset Email',
                        cancelText: 'Cancel',
                        type: 'warning',
                        onConfirm: async () => {
                          try {
                            await authApi.forgotPassword(user?.email || '');
                            notify.success('Password reset email sent! Check your inbox.');
                          } catch (error) {
                            console.error('Error requesting password reset:', error);
                            notify.error('Failed to send password reset email');
                          }
                        },
                        onCancel: () => {},
                      },
                    });
                  }}
                  className="text-sm font-medium text-blue-600 hover:underline"
                >
                  Forgot Password?
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Two-Factor Authentication */}
      <div>
        <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-left">Two-Factor Authentication</h2>
        <div className="bg-white rounded-3xl border border-slate-100 divide-y divide-slate-100">

          {/* Email Codes */}
          <div className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900 text-left">Email Codes</p>
                <p className="text-xs text-slate-500 mt-0.5 text-left">Receive a verification code via email on every login</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.security.twoFactorEnabled && settings.security.twoFactorMethod === 'email'}
                  onChange={(e) => e.target.checked ? handleEnableMethod('email') : handleDisable2FA()}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
            {settings.security.twoFactorEnabled && settings.security.twoFactorMethod === 'email' && (
              <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
                <p className="text-sm text-green-800">
                  Codes will be sent to <strong>{user?.email}</strong>
                </p>
                <button
                  onClick={async () => {
                    try {
                      await authApi.sendEmailCode();
                      notify.success('Test email sent — check your inbox');
                    } catch {
                      notify.error('Failed to send test email');
                    }
                  }}
                  className="px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 rounded-lg border border-blue-200 hover:bg-blue-100"
                >
                  Send Test Email
                </button>
              </div>
            )}
          </div>

          {/* Authenticator App */}
          <div className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-900 text-left">Authenticator App</p>
                <p className="text-xs text-slate-500 mt-0.5 text-left">Use Google Authenticator, Authy, or a similar app</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.security.twoFactorEnabled && settings.security.twoFactorMethod === 'totp'}
                  onChange={(e) => e.target.checked ? handleEnableMethod('totp') : handleDisable2FA()}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {/* TOTP setup area */}
            {is2FASectionExpanded && settings.security.twoFactorMethod === 'totp' && (
              <div className="mt-4 space-y-4">
                <div className="flex gap-6 items-center bg-slate-50 p-4 rounded-lg border border-slate-200">
                  <div className="bg-white p-2 rounded border border-slate-200">
                    {qrCodeData ? (
                      <img src={qrCodeData} alt="2FA QR Code" className="h-32 w-32" />
                    ) : (
                      <div className="h-24 w-24 bg-slate-100 flex items-center justify-center text-slate-600 text-xs text-center p-2">
                        QR Code<br/>Not Available
                      </div>
                    )}
                  </div>
                  <div className="text-xs text-slate-600">
                    <p className="font-semibold">Secret Key (Manual Entry):</p>
                    <code className="block mt-1 font-mono text-blue-600 break-all bg-slate-100 p-2 rounded">
                      {twoFactorSecret || 'Loading...'}
                    </code>
                  </div>
                </div>
                {qrCodeData && !settings.security.twoFactorEnabled && (
                  <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <p className="text-sm font-semibold text-slate-900 mb-2">Confirm Setup</p>
                    <p className="text-xs text-slate-500 mb-3">Enter the 6-digit code from your authenticator app.</p>
                    <div className="flex gap-3 items-center">
                      <input
                        type="text"
                        value={twoFactorToken}
                        onChange={(e) => setTwoFactorToken(e.target.value)}
                        placeholder="000000"
                        className="w-24 px-3 py-2 border border-slate-200 rounded-lg text-center font-mono text-lg"
                        maxLength={6}
                      />
                      <button
                        onClick={handleConfirmTwoFactor}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                      >
                        Confirm
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* SMS - coming soon */}
          <div className="p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-slate-400 text-left">SMS Codes</p>
                <p className="text-xs text-slate-400 mt-0.5 text-left">Receive verification codes via SMS (coming soon)</p>
              </div>
              <label className="relative inline-flex items-center">
                <input type="checkbox" disabled className="sr-only peer" />
                <div className="w-11 h-6 bg-slate-100 rounded-full cursor-not-allowed"></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Login Protection */}
      <div>
        <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-left">Login Protection</h2>
        <div className="bg-white rounded-3xl border border-slate-100 p-5" style={{ boxShadow: "0 4px 20px rgba(0,0,0,0.02)" }}>
          <div className="grid grid-cols-3 gap-8">
            <div className="col-span-1">
              <label className="block text-sm font-semibold text-slate-700 mb-1 text-left">Account Lockout</label>
              <p className="text-xs text-slate-500 leading-relaxed text-left">
                Configure how many failed login attempts trigger a lockout and how long it lasts. Lower values give stronger protection; higher values reduce accidental lockouts.
              </p>
              <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-[11px] font-semibold text-amber-700">Current policy</p>
                <p className="text-[11px] text-amber-600 mt-0.5">
                  Lock after <strong>{settings.security.lockoutMaxAttempts}</strong> failed attempt{settings.security.lockoutMaxAttempts !== 1 ? 's' : ''} for <strong>{settings.security.lockoutDurationMinutes}</strong> minute{settings.security.lockoutDurationMinutes !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="col-span-2 space-y-5">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700">Failed attempts before lockout</label>
                  <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-0.5 rounded-full">
                    {settings.security.lockoutMaxAttempts} {settings.security.lockoutMaxAttempts === 1 ? 'attempt' : 'attempts'}
                  </span>
                </div>
                <input
                  type="range"
                  min={1} max={10} step={1}
                  value={settings.security.lockoutMaxAttempts}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    security: { ...prev.security, lockoutMaxAttempts: parseInt(e.target.value) }
                  }))}
                  className="w-full accent-blue-600"
                />
                <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                  <span>1 (strictest)</span><span>5 (default)</span><span>10 (lenient)</span>
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-slate-700">Lockout duration</label>
                  <span className="text-sm font-semibold text-blue-600 bg-blue-50 px-3 py-0.5 rounded-full">
                    {settings.security.lockoutDurationMinutes} min{settings.security.lockoutDurationMinutes !== 1 ? 's' : ''}
                  </span>
                </div>
                <input
                  type="range"
                  min={1} max={120} step={1}
                  value={settings.security.lockoutDurationMinutes}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    security: { ...prev.security, lockoutDurationMinutes: parseInt(e.target.value) }
                  }))}
                  className="w-full accent-blue-600"
                />
                <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                  <span>1 min</span><span>15 min (default)</span><span>2 hrs</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Trusted device duration (days)</label>
                  <p className="text-[11px] text-slate-500 mb-2">How long a device stays trusted before re-verification. 0 = never trust.</p>
                  <input
                    type="number"
                    min={0} max={365}
                    value={settings.security.trustedDeviceDays}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      security: { ...prev.security, trustedDeviceDays: parseInt(e.target.value) || 0 }
                    }))}
                    className="w-full border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password rotation (days)</label>
                  <p className="text-[11px] text-slate-500 mb-2">Force password change after N days. 0 = never force.</p>
                  <input
                    type="number"
                    min={0} max={365}
                    value={settings.security.requirePasswordRotationDays}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      security: { ...prev.security, requirePasswordRotationDays: parseInt(e.target.value) || 0 }
                    }))}
                    className="w-full border border-slate-200 rounded-lg p-2.5 text-sm outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200">
                <div>
                  <p className="text-sm font-medium text-slate-700">Require strong password</p>
                  <p className="text-xs text-slate-500">Uppercase, lowercase, number, symbol</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.security.requireStrongPassword}
                    onChange={(e) => setSettings(prev => ({
                      ...prev,
                      security: { ...prev.security, requireStrongPassword: e.target.checked }
                    }))}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Active Sessions */}
      <div>
        <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 text-left">Active Access Sessions</h2>
        <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden divide-y divide-slate-100">
          {userSessions.length === 0 ? (
            <div className="p-4 text-center text-slate-500">
              <p className="text-sm">No active sessions found</p>
            </div>
          ) : (
            userSessions.map((session, index) => {
              const isCurrentSession = index === 0; // Assume first session is current
              const deviceName = session.user_agent?.includes('Mobile') ? 'Mobile Device' :
                               session.user_agent?.includes('iOS') ? 'iOS Device' :
                               session.user_agent?.includes('Android') ? 'Android Device' :
                               'Desktop Device';

              return (
                <div key={session.id} className={`p-4 flex items-center justify-between ${isCurrentSession ? 'bg-emerald-50/20' : 'hover:bg-slate-50 transition'}`}>
                  <div className="flex items-center gap-4">
                    <div className={`h-2 w-2 rounded-full ${isCurrentSession ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`}></div>
                    <div>
                      <p className={`text-sm font-medium ${isCurrentSession ? 'text-slate-900' : 'text-slate-700'}`}>
                        {deviceName} {isCurrentSession ? '(Current)' : ''}
                      </p>
                      <p className="text-[10px] font-mono text-slate-400">
                        IP: {session.ip_address || 'Unknown'} | {new Date(session.created_at).toLocaleString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-[9px] font-semibold ${isCurrentSession ? 'text-slate-400' : 'text-slate-500'}`}>
                      {session.is_active ? 'ACTIVE' : 'INACTIVE'}
                    </span>
                    {!isCurrentSession && (
                      <button
                        onClick={() => handleRevokeSession(session.id)}
                        className="text-xs font-medium text-red-600 px-3 py-1 hover:bg-red-50 rounded-lg transition"
                      >
                        Revoke Access
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );

  const renderSystemSection = () => (
    <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
            <SettingsIcon className="text-white" size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 text-left">System Configuration</h3>
            <p className="text-xs text-slate-500 text-left">Configure global system settings</p>
          </div>
        </div>
        <button
          onClick={() => handleSave('system')}
          disabled={saving}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Save System Settings
        </button>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Site Name</label>
            <input
              type="text"
              value={settings.system.siteName}
              onChange={(e) => setSettings(prev => ({ ...prev, system: { ...prev.system, siteName: e.target.value } }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Site URL</label>
            <input
              type="url"
              value={settings.system.siteUrl}
              onChange={(e) => setSettings(prev => ({ ...prev, system: { ...prev.system, siteUrl: e.target.value } }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Support Email</label>
            <input
              type="email"
              value={settings.system.supportEmail}
              onChange={(e) => setSettings(prev => ({ ...prev, system: { ...prev.system, supportEmail: e.target.value } }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Timezone</label>
            <select
              value={settings.system.timezone}
              onChange={(e) => setSettings(prev => ({ ...prev, system: { ...prev.system, timezone: e.target.value } }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="UTC">UTC</option>
              <option value="America/New_York">America/New_York</option>
              <option value="Europe/London">Europe/London</option>
              <option value="Africa/Lagos">Africa/Lagos</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );

  const renderNotificationsSection = () => (
    <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
            <Bell className="text-white" size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 text-left">Change Notification Preferences</h3>
            <p className="text-xs text-slate-500 text-left">Customize which system changes you want to be notified about</p>
          </div>
        </div>
      </div>
      <div className="p-6">
        <ChangeNotificationPreferences onSave={() => {}} />
      </div>
    </div>
  );

  const renderCompanySection = () => (
    <div className="bg-white rounded-3xl border border-slate-100 overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-purple-600 rounded-lg flex items-center justify-center">
            <Building2 className="text-white" size={16} />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-900 text-left">Company Settings</h3>
            <p className="text-xs text-slate-500 text-left">Manage company information and compliance settings</p>
          </div>
        </div>
        <button
          onClick={() => handleSave('company')}
          disabled={saving}
          className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Save Company Settings
        </button>
      </div>
      <div className="p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Company Name</label>
            <input
              type="text"
              value={settings.company.companyName}
              onChange={(e) => setSettings(prev => ({ ...prev, company: { ...prev.company, companyName: e.target.value } }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Company Email</label>
            <input
              type="email"
              value={settings.company.companyEmail}
              onChange={(e) => setSettings(prev => ({ ...prev, company: { ...prev.company, companyEmail: e.target.value } }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Industry</label>
            <input
              type="text"
              value={settings.company.industry}
              onChange={(e) => setSettings(prev => ({ ...prev, company: { ...prev.company, industry: e.target.value } }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Compliance Deadline</label>
            <input
              type="date"
              value={settings.company.complianceDeadline}
              onChange={(e) => setSettings(prev => ({ ...prev, company: { ...prev.company, complianceDeadline: e.target.value } }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="col-span-1 md:col-span-2">
            <label className="flex items-center space-x-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
              <input
                type="checkbox"
                checked={settings.company.autoAssignFrameworks}
                onChange={(e) => setSettings(prev => ({ ...prev, company: { ...prev.company, autoAssignFrameworks: e.target.checked } }))}
                className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
              />
              <div>
                <span className="text-sm font-medium text-slate-800">Auto-assign frameworks to new users</span>
                <p className="text-xs text-slate-500">Automatically assign compliance frameworks when new users join</p>
              </div>
            </label>
          </div>
        </div>
      </div>
    </div>
  );

  const tabs = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'security', label: 'Security', icon: Shield },
    { id: 'notifications', label: 'Notifications', icon: Bell },
    ...(isCompanyUser ? [{ id: 'company', label: 'Company', icon: Building2 }] : []),
    ...(isSystemAdmin ? [{ id: 'system', label: 'System', icon: SettingsIcon }] : [])
  ];

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
    // Update URL without causing a page reload
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('tab', tabId);
    setSearchParams(newSearchParams);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-6">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900 text-left">Account Settings</h1><PageInfoButton /></div>
          <p className="text-slate-500 text-sm mt-0.5 text-left">Manage your account and system preferences</p>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white rounded-lg border border-slate-200 p-1 inline-flex gap-1 mb-6">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'text-slate-600 hover:bg-slate-50'
                }`}
              >
                <Icon size={16} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* Tab Content */}
        {activeTab === 'profile' && renderProfileSection()}
        {activeTab === 'security' && renderSecuritySection()}
        {activeTab === 'system' && isSystemAdmin && renderSystemSection()}
        {activeTab === 'notifications' && renderNotificationsSection()}
        {activeTab === 'company' && isCompanyUser && renderCompanySection()}
      </div>
    </div>
  );
};

export default AccountSettings;
